import { FeaturePermissions, UserVoiceActions, useUserInfo, useUserVoices } from "@next-platform/client-common-core";
import { Spinner } from "@nextui-org/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PageLayout, PageSize } from "@/components";
import TextStyles from "@/components/common/TextStyles";
import { Text } from "@/components/customComponents";
import { ROUTES } from "@/constants";
import UnlockCustomVoicesModal from "@/pages/CreateCharacter/v2/components/UnlockCustomVoicesModal";
import useAuth from "@/services/useAuth";
import { cn } from "@/utils";

import { ButtonGroup } from "./ButtonGroup";
import CloneVoiceForm from "./components/CloneVoiceForm";
import { CloneVoiceFormProvider } from "./components/CloneVoiceFormContext";
import useCloneVoiceHandler from "./useCloneVoiceHandler";
import { shouldRedirectFromReclone } from "./utils/shouldRedirectFromReclone";

enum VoiceTabNames {
  CLONING = "cloning",
  MIXING = "mixing",
}

const isRecloneVoiceFetching = (
  recloneId: string | null,
  isVoiceLoading: boolean,
  loadingType: ReturnType<typeof useUserVoices>["loadingType"],
  voice: ReturnType<typeof useUserVoices>["voice"],
): boolean => {
  return Boolean(recloneId && isVoiceLoading && loadingType === UserVoiceActions.GET_USER_VOICE_BY_ID && voice?.id !== recloneId);
};

export default function CloneVoice() {
  const { isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stateActiveTab] = useState<VoiceTabNames>(VoiceTabNames.CLONING);
  const { permissions } = useUserInfo();
  const { getUserVoiceById, voice, isLoading: isVoiceLoading, loadingType } = useUserVoices();
  const returnTo = searchParams.get("returnTo");
  const recloneId = searchParams.get("recloneId");

  const hasCustomVoicesAccess = permissions?.includes(FeaturePermissions.TTS_CUSTOM_VOICES) ?? false;
  const [isShowUnlockModal, setIsShowUnlockModal] = useState(!hasCustomVoicesAccess);
  const onClone = useCloneVoiceHandler(recloneId, returnTo, hasCustomVoicesAccess);
  const recloneVoice = recloneId ? voice : undefined;

  useEffect(() => {
    if (recloneId) void getUserVoiceById(recloneId);
  }, [recloneId, getUserVoiceById]);

  useEffect(() => {
    if (!shouldRedirectFromReclone(recloneId, voice)) return;
    navigate(ROUTES.voices, { replace: true });
  }, [recloneId, voice, navigate]);

  const buttonGroupActions = useMemo(
    () => [
      // TODO(Arthur):[NX-5139] Enable cloning tab when mixing is implemented
      // {
      //   label: "Cloning",
      //   active: stateActiveTab === VoiceTabNames.CLONING,
      //   onClick: () => setStateActiveTab(VoiceTabNames.CLONING),
      // },
      // TODO(Arthur):[NX-5139] Enable when mixing is implemented
      // {
      //   label: "Mixing",
      //   active: activeTab === VoiceTabNames.MIXING,
      //   onClick: () => setActiveTab(VoiceTabNames.MIXING),
      // },
    ],
    [],
  );

  const isFetchingRecloneVoice = isRecloneVoiceFetching(recloneId, isVoiceLoading, loadingType, voice);
  if (isLoading || isFetchingRecloneVoice) return <Spinner className={TextStyles.spinner} />;

  return (
    <PageLayout size={PageSize.Medium}>
      <UnlockCustomVoicesModal isOpen={isShowUnlockModal} onClose={setIsShowUnlockModal} />
      <div className="flex flex-col">
        <div className="flex justify-start">
          <ButtonGroup actions={buttonGroupActions} />
        </div>
        <div>
          <div className={cn("flex flex-col relative bg-gray-1 dark:bg-gray-3 p-lg max-mob:p-md rounded-[16px] border border-solid border-gray-5")}>
            {stateActiveTab === VoiceTabNames.CLONING ? (
              <CloneVoiceFormProvider>
                <CloneVoiceForm
                  onClone={onClone}
                  name={recloneVoice?.name}
                  description={recloneVoice?.description}
                  language={recloneVoice?.language}
                  voiceClipPlaceholders={recloneVoice?.sample_audio_files}
                />
              </CloneVoiceFormProvider>
            ) : (
              <div className="flex items-center justify-center min-h-[400px]">
                <Text variant={"lg"}>To be implemented</Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
